import { useMemo } from 'react';
import {
  Zap,
  Thermometer,
  Fan,
  Plug,
  Battery,
  AlertTriangle,
  DollarSign,
  Gauge,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '../ui';
import { DatacenterConfig } from '../../types/configurator';
import {
  calculatePowerRequirements,
  recommendCooling,
  recommendCircuits,
  PowerCalculation,
  CoolingRecommendation,
} from '../../utils/powerCooling';

interface PowerCoolingSummaryProps {
  config: DatacenterConfig;
  compact?: boolean;
}

export function PowerCoolingSummary({ config, compact = false }: PowerCoolingSummaryProps) {
  const powerCalc = useMemo(() => {
    return calculatePowerRequirements(
      config.nodes,
      config.containerConfig?.infrastructure || [],
      config.containerConfig
    );
  }, [config.nodes, config.containerConfig]);

  const coolingRecs = useMemo(() => recommendCooling(powerCalc), [powerCalc]);
  const circuitRecs = useMemo(() => recommendCircuits(powerCalc), [powerCalc]);

  if (config.nodes.length === 0) {
    return null;
  }

  if (compact) {
    return <CompactView powerCalc={powerCalc} />;
  }

  return (
    <div className="space-y-4">
      {/* Power Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            <CardTitle>Power & Cooling Analysis</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* IT Load */}
            <div className="rounded-lg bg-blue-50 p-4">
              <div className="flex items-center gap-2 text-blue-600">
                <Plug className="h-4 w-4" />
                <span className="text-sm font-medium">IT Load</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-blue-900">
                {powerCalc.itLoadWatts.toLocaleString()}W
              </div>
              <div className="text-xs text-blue-600">
                Peak: {powerCalc.peakWatts.toLocaleString()}W
              </div>
            </div>

            {/* Heat Output */}
            <div className="rounded-lg bg-orange-50 p-4">
              <div className="flex items-center gap-2 text-orange-600">
                <Thermometer className="h-4 w-4" />
                <span className="text-sm font-medium">Heat Output</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-orange-900">
                {Math.round(powerCalc.btuPerHour).toLocaleString()}
              </div>
              <div className="text-xs text-orange-600">BTU/hr</div>
            </div>

            {/* Cooling Required */}
            <div className="rounded-lg bg-cyan-50 p-4">
              <div className="flex items-center gap-2 text-cyan-600">
                <Fan className="h-4 w-4" />
                <span className="text-sm font-medium">Cooling Required</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-cyan-900">
                {powerCalc.recommendedCoolingTons.toFixed(2)}
              </div>
              <div className="text-xs text-cyan-600">tons (w/margin)</div>
            </div>

            {/* Annual Cost */}
            <div className="rounded-lg bg-green-50 p-4">
              <div className="flex items-center gap-2 text-green-600">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm font-medium">Annual Energy</span>
              </div>
              <div className="mt-2 text-2xl font-bold text-green-900">
                ${Math.round(powerCalc.annualCostUSD).toLocaleString()}
              </div>
              <div className="text-xs text-green-600">
                {Math.round(powerCalc.annualKwh).toLocaleString()} kWh/yr
              </div>
            </div>
          </div>

          {/* Warnings */}
          {powerCalc.warnings.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-800">Considerations</div>
                  <ul className="mt-1 space-y-1">
                    {powerCalc.warnings.map((warning, i) => (
                      <li key={i} className="text-sm text-amber-700">
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Breakdown */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Electrical Requirements */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Plug className="h-4 w-4 text-hermit-600" />
              <CardTitle className="text-sm">Electrical Requirements</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">@ 120V (residential)</dt>
                <dd className="font-mono">{powerCalc.ampsAt120V.toFixed(1)}A</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">@ 208V (3-phase)</dt>
                <dd className="font-mono">{powerCalc.ampsAt208V.toFixed(1)}A</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">@ 230V (EU)</dt>
                <dd className="font-mono">{powerCalc.ampsAt230V.toFixed(1)}A</dd>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <dt className="text-gray-500">20A circuits needed</dt>
                  <dd className="font-medium">{powerCalc.circuitsNeeded20A}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">30A circuits needed</dt>
                  <dd className="font-medium">{powerCalc.circuitsNeeded30A}</dd>
                </div>
              </div>
            </dl>

            {/* Circuit Recommendations */}
            {circuitRecs.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <div className="text-xs font-medium text-gray-500 mb-2">Recommended</div>
                {circuitRecs.map((rec, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 text-xs mb-1"
                  >
                    <span className="text-gray-700">
                      {rec.quantity}x {rec.amperage}A {rec.voltage}V {rec.phase}
                    </span>
                    <span className="text-gray-500">{rec.wireGauge}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* UPS & PDU */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Battery className="h-4 w-4 text-hermit-600" />
              <CardTitle className="text-sm">UPS & PDU Sizing</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Recommended UPS</dt>
                <dd className="font-mono">
                  {(powerCalc.recommendedUpsVA / 1000).toFixed(1)} kVA
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Runtime @ 50% load</dt>
                <dd className="font-medium">{powerCalc.runtimeMinutesAt50Percent} min</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Runtime @ 100% load</dt>
                <dd className="font-medium">{powerCalc.runtimeMinutesAt100Percent} min</dd>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Recommended PDU</dt>
                  <dd className="font-mono">{powerCalc.recommendedPduAmps}A</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Outlets needed</dt>
                  <dd className="font-medium">{powerCalc.outletsNeeded}</dd>
                </div>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Cooling Recommendations */}
        <Card className="md:col-span-2">
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Fan className="h-4 w-4 text-hermit-600" />
              <CardTitle className="text-sm">Cooling Options</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {coolingRecs.map((rec, i) => (
                <CoolingOptionCard key={i} recommendation={rec} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Node Power Breakdown */}
        {powerCalc.nodeBreakdown.length > 0 && (
          <Card className="md:col-span-2">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-hermit-600" />
                <CardTitle className="text-sm">Power by Node</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {powerCalc.nodeBreakdown.map((node, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-32 truncate text-sm text-gray-700">{node.name}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-hermit-500"
                          style={{ width: `${Math.min(node.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-20 text-right text-sm font-mono text-gray-600">
                      {node.watts}W
                    </div>
                    <div className="w-12 text-right text-xs text-gray-400">
                      {node.percentage.toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Efficiency Metrics */}
        <Card className="md:col-span-2">
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-hermit-600" />
              <CardTitle className="text-sm">Efficiency Metrics</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <div className="text-xs text-gray-500">PUE</div>
                <div className="text-lg font-bold text-gray-900">
                  {powerCalc.pue.toFixed(2)}
                </div>
                <div className="text-xs text-gray-400">
                  {powerCalc.pue <= 1.3 ? 'Excellent' : powerCalc.pue <= 1.5 ? 'Good' : 'Typical'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Cooling Overhead</div>
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(powerCalc.coolingWatts)}W
                </div>
                <div className="text-xs text-gray-400">
                  {((powerCalc.coolingWatts / powerCalc.itLoadWatts) * 100).toFixed(0)}% of IT load
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Other Overhead</div>
                <div className="text-lg font-bold text-gray-900">
                  {Math.round(powerCalc.overheadWatts)}W
                </div>
                <div className="text-xs text-gray-400">Lighting, controls</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Carbon Footprint</div>
                <div className="text-lg font-bold text-gray-900">
                  {((powerCalc.annualKwh * 0.42) / 1000).toFixed(1)}
                </div>
                <div className="text-xs text-gray-400">tonnes CO₂/yr (avg grid)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CompactView({ powerCalc }: { powerCalc: PowerCalculation }) {
  return (
    <div className="grid grid-cols-4 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="text-center">
        <Zap className="mx-auto h-4 w-4 text-amber-500" />
        <div className="mt-1 text-lg font-bold text-gray-900">
          {powerCalc.totalWatts.toLocaleString()}W
        </div>
        <div className="text-xs text-gray-500">Total Power</div>
      </div>
      <div className="text-center">
        <Thermometer className="mx-auto h-4 w-4 text-orange-500" />
        <div className="mt-1 text-lg font-bold text-gray-900">
          {Math.round(powerCalc.btuPerHour / 1000)}k
        </div>
        <div className="text-xs text-gray-500">BTU/hr</div>
      </div>
      <div className="text-center">
        <Fan className="mx-auto h-4 w-4 text-cyan-500" />
        <div className="mt-1 text-lg font-bold text-gray-900">
          {powerCalc.recommendedCoolingTons.toFixed(1)}
        </div>
        <div className="text-xs text-gray-500">Tons Cooling</div>
      </div>
      <div className="text-center">
        <DollarSign className="mx-auto h-4 w-4 text-green-500" />
        <div className="mt-1 text-lg font-bold text-gray-900">
          ${Math.round(powerCalc.annualCostUSD / 12)}
        </div>
        <div className="text-xs text-gray-500">/month</div>
      </div>
    </div>
  );
}

function CoolingOptionCard({ recommendation }: { recommendation: CoolingRecommendation }) {
  const typeColors: Record<CoolingRecommendation['type'], string> = {
    none: 'bg-gray-100 text-gray-600',
    portable: 'bg-blue-100 text-blue-600',
    split: 'bg-cyan-100 text-cyan-600',
    inrow: 'bg-purple-100 text-purple-600',
    crac: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between">
        <div>
          <Badge className={typeColors[recommendation.type]}>{recommendation.type}</Badge>
          <div className="mt-1 font-medium text-gray-900">{recommendation.name}</div>
        </div>
        {recommendation.quantity > 0 && (
          <div className="text-right">
            <div className="text-sm font-bold text-gray-900">
              ${recommendation.totalCost.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">×{recommendation.quantity}</div>
          </div>
        )}
      </div>
      <div className="mt-2 text-xs text-gray-500">{recommendation.description}</div>
      {recommendation.btuCapacity > 0 && (
        <div className="mt-1 text-xs text-gray-400">
          {recommendation.btuCapacity.toLocaleString()} BTU/hr capacity
        </div>
      )}
    </div>
  );
}
